import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, UserIcon, MailIcon, PhoneIcon, KeyIcon } from 'lucide-react';
import { AdminApproval } from '../../components/common/AdminApproval';
interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  status: 'active' | 'inactive' | 'locked';
  lastLogin: string;
  createdAt: string;
}
export function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [pendingAction, setPendingAction] = useState<'add' | 'edit' | 'delete' | 'reset' | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    status: 'active' as 'active' | 'inactive' | 'locked',
    password: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  // Sample data
  useEffect(() => {
    setRoles(['Administrator', 'Manager', 'Operator', 'Auditor']);
    setUsers([{
      id: 1,
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      phone: '+1234567890',
      role: 'Administrator',
      status: 'active',
      lastLogin: '2023-05-10 09:45:22',
      createdAt: '2023-01-15'
    }, {
      id: 2,
      firstName: 'Jane',
      lastName: 'Smith',
      email: 'jane.smith@example.com',
      phone: '+1987654321',
      role: 'Manager',
      status: 'active',
      lastLogin: '2023-05-09 14:30:10',
      createdAt: '2023-01-20'
    }, {
      id: 3,
      firstName: 'Michael',
      lastName: 'Johnson',
      email: 'michael.johnson@example.com',
      phone: '+1122334455',
      role: 'Operator',
      status: 'inactive',
      lastLogin: '2023-04-25 11:15:45',
      createdAt: '2023-02-05'
    }, {
      id: 4,
      firstName: 'Sarah',
      lastName: 'Williams',
      email: 'sarah.williams@example.com',
      phone: '+1555666777',
      role: 'Auditor',
      status: 'locked',
      lastLogin: '2023-04-15 16:20:30',
      createdAt: '2023-03-10'
    }]);
  }, []);
  const filteredUsers = users.filter(user => user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) || user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase()) || user.role.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleAddUser = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: roles[0],
      status: 'active',
      password: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setShowAddForm(true);
  };
  const handleEditUser = (user: User) => {
    setCurrentUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone,
      role: user.role,
      status: user.status,
      password: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setShowEditForm(true);
  };
  const handleDeleteUser = (user: User) => {
    setCurrentUser(user);
    setShowDeleteConfirm(true);
  };
  const handleResetPassword = (user: User) => {
    setCurrentUser(user);
    setFormData({
      ...formData,
      password: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setShowResetPassword(true);
  };
  const validateForm = () => {
    if ((showAddForm || showResetPassword) && formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }
    if ((showAddForm || showResetPassword) && formData.password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return false;
    }
    setPasswordError('');
    return true;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    if (showAddForm) {
      setPendingAction('add');
    } else if (showEditForm) {
      setPendingAction('edit');
    } else if (showResetPassword) {
      setPendingAction('reset');
    }
    setShowApprovalModal(true);
    setShowAddForm(false);
    setShowEditForm(false);
    setShowResetPassword(false);
  };
  const handleApprove = () => {
    if (pendingAction === 'add') {
      const newUser: User = {
        id: users.length + 1,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        status: formData.status,
        lastLogin: 'Never',
        createdAt: new Date().toISOString().split('T')[0]
      };
      setUsers([...users, newUser]);
    } else if (pendingAction === 'edit' && currentUser) {
      const updatedUsers = users.map(user => user.id === currentUser.id ? {
        ...user,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        status: formData.status
      } : user);
      setUsers(updatedUsers);
    } else if (pendingAction === 'delete' && currentUser) {
      const updatedUsers = users.filter(user => user.id !== currentUser.id);
      setUsers(updatedUsers);
    }
    // For reset password, we would typically call an API
    setShowApprovalModal(false);
    setPendingAction(null);
    setCurrentUser(null);
    setShowDeleteConfirm(false);
  };
  const handleReject = () => {
    setShowApprovalModal(false);
    setPendingAction(null);
    setShowDeleteConfirm(false);
  };
  const confirmDelete = () => {
    setPendingAction('delete');
    setShowDeleteConfirm(false);
    setShowApprovalModal(true);
  };
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'locked':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-874">
      <div className="flex justify-between items-center" data-id="element-875">
        <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-876">User Management</h1>
        <button onClick={handleAddUser} className="flex items-center px-4 py-2 bg-[#F58220] text-white rounded-md hover:bg-[#e07619]" data-id="element-877">
          <PlusIcon size={16} className="mr-2" data-id="element-878" />
          Add New User
        </button>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4" data-id="element-879">
        <div className="mb-4 relative" data-id="element-880">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-881">
            <SearchIcon size={16} className="text-gray-400" data-id="element-882" />
          </div>
          <input type="text" placeholder="Search users..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-883" />
        </div>
        <div className="overflow-x-auto" data-id="element-884">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-885">
            <thead className="bg-gray-50" data-id="element-886">
              <tr data-id="element-887">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-888">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-889">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-890">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-891">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-892">
                  Last Login
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-893">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-894">
              {filteredUsers.map(user => <tr key={user.id} data-id="element-895">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-896">
                    <div className="flex items-center" data-id="element-897">
                      <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center" data-id="element-898">
                        <UserIcon size={20} className="text-gray-500" data-id="element-899" />
                      </div>
                      <div className="ml-4" data-id="element-900">
                        <div className="text-sm font-medium text-gray-900" data-id="element-901">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-902">
                          {user.phone}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-903">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-904">
                    {user.role}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-905">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(user.status)}`} data-id="element-906">
                      {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-907">
                    {user.lastLogin}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-908">
                    <div className="flex space-x-2" data-id="element-909">
                      <button onClick={() => handleEditUser(user)} className="text-blue-600 hover:text-blue-900" title="Edit User" data-id="element-910">
                        <EditIcon size={16} data-id="element-911" />
                      </button>
                      <button onClick={() => handleResetPassword(user)} className="text-yellow-600 hover:text-yellow-900" title="Reset Password" data-id="element-912">
                        <KeyIcon size={16} data-id="element-913" />
                      </button>
                      <button onClick={() => handleDeleteUser(user)} className="text-red-600 hover:text-red-900" title="Delete User" data-id="element-914">
                        <TrashIcon size={16} data-id="element-915" />
                      </button>
                    </div>
                  </td>
                </tr>)}
              {filteredUsers.length === 0 && <tr data-id="element-916">
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-917">
                    No users found
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
      {/* Add User Form */}
      {showAddForm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-918">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-xl p-6" data-id="element-919">
            <div className="flex justify-between items-center mb-4" data-id="element-920">
              <h3 className="text-lg font-semibold text-[#1A1F36]" data-id="element-921">
                Add New User
              </h3>
              <button onClick={() => setShowAddForm(false)} className="text-gray-500 hover:text-gray-700" data-id="element-922">
                <XCircleIcon size={20} data-id="element-923" />
              </button>
            </div>
            <form onSubmit={handleSubmit} data-id="element-924">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-925">
                <div data-id="element-926">
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-927">
                    First Name
                  </label>
                  <input type="text" id="firstName" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.firstName} onChange={e => setFormData({
                ...formData,
                firstName: e.target.value
              })} data-id="element-928" />
                </div>
                <div data-id="element-929">
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-930">
                    Last Name
                  </label>
                  <input type="text" id="lastName" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.lastName} onChange={e => setFormData({
                ...formData,
                lastName: e.target.value
              })} data-id="element-931" />
                </div>
              </div>
              <div className="mb-4" data-id="element-932">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-933">
                  Email
                </label>
                <div className="relative" data-id="element-934">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-935">
                    <MailIcon size={16} className="text-gray-400" data-id="element-936" />
                  </div>
                  <input type="email" id="email" required className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} data-id="element-937" />
                </div>
              </div>
              <div className="mb-4" data-id="element-938">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-939">
                  Phone
                </label>
                <div className="relative" data-id="element-940">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-941">
                    <PhoneIcon size={16} className="text-gray-400" data-id="element-942" />
                  </div>
                  <input type="text" id="phone" required className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} data-id="element-943" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-944">
                <div data-id="element-945">
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-946">
                    Role
                  </label>
                  <select id="role" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.role} onChange={e => setFormData({
                ...formData,
                role: e.target.value
              })} data-id="element-947">
                    {roles.map(role => <option key={role} value={role} data-id="element-948">
                        {role}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-949">
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-950">
                    Status
                  </label>
                  <select id="status" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.status} onChange={e => setFormData({
                ...formData,
                status: e.target.value as 'active' | 'inactive' | 'locked'
              })} data-id="element-951">
                    <option value="active" data-id="element-952">Active</option>
                    <option value="inactive" data-id="element-953">Inactive</option>
                    <option value="locked" data-id="element-954">Locked</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-955">
                <div data-id="element-956">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-957">
                    Password
                  </label>
                  <input type="password" id="password" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.password} onChange={e => setFormData({
                ...formData,
                password: e.target.value
              })} data-id="element-958" />
                </div>
                <div data-id="element-959">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-960">
                    Confirm Password
                  </label>
                  <input type="password" id="confirmPassword" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.confirmPassword} onChange={e => setFormData({
                ...formData,
                confirmPassword: e.target.value
              })} data-id="element-961" />
                </div>
              </div>
              {passwordError && <div className="mb-4 text-red-500 text-sm" data-id="element-962">{passwordError}</div>}
              <div className="mt-6 flex justify-end space-x-3" data-id="element-963">
                <button type="button" onClick={() => setShowAddForm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-964">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-[#F58220] text-white rounded-md hover:bg-[#e07619]" data-id="element-965">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>}
      {/* Edit User Form */}
      {showEditForm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-966">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-xl p-6" data-id="element-967">
            <div className="flex justify-between items-center mb-4" data-id="element-968">
              <h3 className="text-lg font-semibold text-[#1A1F36]" data-id="element-969">
                Edit User
              </h3>
              <button onClick={() => setShowEditForm(false)} className="text-gray-500 hover:text-gray-700" data-id="element-970">
                <XCircleIcon size={20} data-id="element-971" />
              </button>
            </div>
            <form onSubmit={handleSubmit} data-id="element-972">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-973">
                <div data-id="element-974">
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-975">
                    First Name
                  </label>
                  <input type="text" id="firstName" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.firstName} onChange={e => setFormData({
                ...formData,
                firstName: e.target.value
              })} data-id="element-976" />
                </div>
                <div data-id="element-977">
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-978">
                    Last Name
                  </label>
                  <input type="text" id="lastName" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.lastName} onChange={e => setFormData({
                ...formData,
                lastName: e.target.value
              })} data-id="element-979" />
                </div>
              </div>
              <div className="mb-4" data-id="element-980">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-981">
                  Email
                </label>
                <div className="relative" data-id="element-982">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-983">
                    <MailIcon size={16} className="text-gray-400" data-id="element-984" />
                  </div>
                  <input type="email" id="email" required className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} data-id="element-985" />
                </div>
              </div>
              <div className="mb-4" data-id="element-986">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-987">
                  Phone
                </label>
                <div className="relative" data-id="element-988">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-989">
                    <PhoneIcon size={16} className="text-gray-400" data-id="element-990" />
                  </div>
                  <input type="text" id="phone" required className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} data-id="element-991" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-992">
                <div data-id="element-993">
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-994">
                    Role
                  </label>
                  <select id="role" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.role} onChange={e => setFormData({
                ...formData,
                role: e.target.value
              })} data-id="element-995">
                    {roles.map(role => <option key={role} value={role} data-id="element-996">
                        {role}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-997">
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-998">
                    Status
                  </label>
                  <select id="status" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.status} onChange={e => setFormData({
                ...formData,
                status: e.target.value as 'active' | 'inactive' | 'locked'
              })} data-id="element-999">
                    <option value="active" data-id="element-1000">Active</option>
                    <option value="inactive" data-id="element-1001">Inactive</option>
                    <option value="locked" data-id="element-1002">Locked</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-1003">
                <button type="button" onClick={() => setShowEditForm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1004">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-[#F58220] text-white rounded-md hover:bg-[#e07619]" data-id="element-1005">
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>}
      {/* Reset Password Form */}
      {showResetPassword && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1006">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6" data-id="element-1007">
            <div className="flex justify-between items-center mb-4" data-id="element-1008">
              <h3 className="text-lg font-semibold text-[#1A1F36]" data-id="element-1009">
                Reset Password
              </h3>
              <button onClick={() => setShowResetPassword(false)} className="text-gray-500 hover:text-gray-700" data-id="element-1010">
                <XCircleIcon size={20} data-id="element-1011" />
              </button>
            </div>
            <form onSubmit={handleSubmit} data-id="element-1012">
              <div className="mb-4" data-id="element-1013">
                <p className="text-gray-600 mb-4" data-id="element-1014">
                  Reset password for {currentUser?.firstName}{' '}
                  {currentUser?.lastName}
                </p>
              </div>
              <div className="mb-4" data-id="element-1015">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1016">
                  New Password
                </label>
                <input type="password" id="password" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.password} onChange={e => setFormData({
              ...formData,
              password: e.target.value
            })} data-id="element-1017" />
              </div>
              <div className="mb-4" data-id="element-1018">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1019">
                  Confirm New Password
                </label>
                <input type="password" id="confirmPassword" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.confirmPassword} onChange={e => setFormData({
              ...formData,
              confirmPassword: e.target.value
            })} data-id="element-1020" />
              </div>
              {passwordError && <div className="mb-4 text-red-500 text-sm" data-id="element-1021">{passwordError}</div>}
              <div className="mt-6 flex justify-end space-x-3" data-id="element-1022">
                <button type="button" onClick={() => setShowResetPassword(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1023">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-[#F58220] text-white rounded-md hover:bg-[#e07619]" data-id="element-1024">
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>}
      {/* Delete Confirmation */}
      {showDeleteConfirm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1025">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6" data-id="element-1026">
            <div className="flex items-center mb-4 text-red-600" data-id="element-1027">
              <AlertCircleIcon size={24} className="mr-2" data-id="element-1028" />
              <h3 className="text-lg font-semibold" data-id="element-1029">Delete User</h3>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-1030">
              Are you sure you want to delete the user "{currentUser?.firstName}{' '}
              {currentUser?.lastName}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3" data-id="element-1031">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-1032">
                Cancel
              </button>
              <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-1033">
                Delete
              </button>
            </div>
          </div>
        </div>}
      {/* Admin Approval Modal */}
      <AdminApproval isVisible={showApprovalModal} onClose={() => setShowApprovalModal(false)} onApprove={handleApprove} onReject={handleReject} title={pendingAction === 'add' ? 'Approve New User' : pendingAction === 'edit' ? 'Approve User Update' : pendingAction === 'delete' ? 'Approve User Deletion' : 'Approve Password Reset'} description={pendingAction === 'add' ? `Please review and approve the creation of the new user "${formData.firstName} ${formData.lastName}".` : pendingAction === 'edit' ? `Please review and approve the updates to the user "${formData.firstName} ${formData.lastName}".` : pendingAction === 'delete' ? `Please review and approve the deletion of the user "${currentUser?.firstName} ${currentUser?.lastName}".` : `Please review and approve the password reset for "${currentUser?.firstName} ${currentUser?.lastName}".`} data-id="element-1034" />
    </div>;
}